import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

const TooltipContainer = styled.span`
  position: relative;
  display: inline-block;
`;

// Rendered into document.body so it escapes any ancestor with `overflow` set
// (modals, scrollable forms, sticky toolbars). Positioned with `position: fixed`
// against viewport coordinates derived from the trigger's bounding rect.
const TooltipContent = styled.div`
  position: fixed;
  background-color: var(--text-primary);
  color: var(--text-inverse);
  padding: 0.5rem 0.75rem;
  border-radius: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  white-space: nowrap;
  z-index: 10000;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  visibility: ${props => props.$visible ? 'visible' : 'hidden'};
  transition: opacity 0.15s ease, visibility 0.15s ease;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.13);

  /* Center horizontally on the trigger via translateX, and flip up when
     position='top' by translating its full height up. */
  transform: translateX(-50%) ${props => props.$position === 'top' ? 'translateY(-100%)' : ''};

  &::after {
    content: '';
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    ${props => props.$position === 'top' ? `
      top: 100%;
      border-top-color: var(--text-primary);
    ` : `
      bottom: 100%;
      border-bottom-color: var(--text-primary);
    `}
  }

  /* No hover means no tooltip. Long-press tooltips on touch are noisy. */
  @media (max-width: 768px), (hover: none) {
    display: none !important;
  }
`;

const GAP_PX = 8;

function Tooltip({ children, content, text, position = 'bottom', className }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ left: 0, top: 0 });
  const containerRef = useRef(null);

  const measure = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = rect.left + rect.width / 2;
    const top = position === 'top'
      ? rect.top - GAP_PX
      : rect.bottom + GAP_PX;
    setCoords({ left, top });
  }, [position]);

  // Re-measure on scroll/resize while visible — fixed positioning needs to track
  // the trigger if the underlying layout moves (e.g. modal content scrolling).
  useEffect(() => {
    if (!visible) return;
    measure();
    const onChange = () => measure();
    window.addEventListener('scroll', onChange, true);
    window.addEventListener('resize', onChange);
    return () => {
      window.removeEventListener('scroll', onChange, true);
      window.removeEventListener('resize', onChange);
    };
  }, [visible, measure]);

  const label = content ?? text;
  const showPortal = visible && typeof document !== 'undefined' && label;

  return (
    <TooltipContainer
      ref={containerRef}
      className={className}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children}
      {showPortal && createPortal(
        <TooltipContent
          $visible={visible}
          $position={position}
          style={{ left: coords.left, top: coords.top }}
          role="tooltip"
        >
          {label}
        </TooltipContent>,
        document.body,
      )}
    </TooltipContainer>
  );
}

export default Tooltip;
